import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Checkbox,
  TableSortLabel,
  Fab,
  Autocomplete,
  FormControl,
  Tooltip,
  Tabs,
  Tab,
  List,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
  LocationOn as LocationOnIcon,
  CheckCircle as CheckCircleIcon,
  Note as NoteIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { dataService } from '../services/dataService';
import { colors } from '../theme';
import type { SupplierMaster } from '../types';
import * as XLSX from 'xlsx';

// ============================================================================
// WARNING: PERSISTENCIA TEMPORAL CON LOCALSTORAGE - MIGRAR A BACKEND EN PRODUCCIÓN
// ============================================================================
// Este sistema usa localStorage para persistir datos en el navegador.
// LIMITACIONES:
// - Los datos solo existen en el navegador local del usuario
// - No se comparten entre diferentes usuarios
// - Se pierden si el usuario limpia el navegador
// - Límite de almacenamiento: ~5-10MB
//
// TODO PARA PRODUCCIÓN:
// 1. Crear endpoints en backend:
//    - POST /api/suppliers/:id/categories (asignar categorías)
//    - GET /api/suppliers/:id/categories (obtener categorías)
//    - POST /api/suppliers/:id/notes (crear nota)
//    - GET /api/suppliers/:id/notes (listar notas)
//    - DELETE /api/notes/:noteId (eliminar nota)
// 2. Implementar autenticación para obtener usuario real en notas
// 3. Agregar timestamps del servidor
// 4. Implementar validación y sanitización en backend
// 5. Migrar estas funciones a un servicio API (ej: supplierService.ts)
// ============================================================================

const STORAGE_KEYS = {
  ASSIGNED_CATEGORIES: 'interbarge_supplier_categories',
  SUPPLIER_NOTES: 'interbarge_supplier_notes',
} as const;

type Order = 'asc' | 'desc';
type OrderBy = keyof SupplierData;

interface SupplierData extends SupplierMaster {
  supplier: string; // Alias de razonSocial para compatibilidad
  orderCount: number;
  categoriesCount: number;
  locationsCount: number;
  firstOrderDate: Date;
  lastOrderDate: Date;
}

const ActiveSuppliers: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<SupplierData[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<SupplierData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Selección múltiple
  const [selected, setSelected] = useState<Set<string>>(new Set());
  
  // Ordenamiento
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('orderCount');
  
  // Modal de asignación de categorías
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  
  // Tabs del modal de detalle
  const [currentTab, setCurrentTab] = useState<number>(0);
  
  // Sistema de notas
  const [notes, setNotes] = useState<Record<string, any[]>>({});
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteCategory, setNewNoteCategory] = useState<string>('general');

  // ============================================================================
  // FUNCIONES DE PERSISTENCIA (LOCALSTORAGE)
  // WARNING: Estas funciones deben ser reemplazadas por llamadas a API en producción
  // ============================================================================
  
  /**
   * Carga las categorías asignadas desde localStorage
   * TODO PRODUCCIÓN: Reemplazar por GET /api/suppliers/categories
   */
  const loadAssignedCategoriesFromStorage = (): Record<string, string[]> => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ASSIGNED_CATEGORIES);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error loading assigned categories from localStorage:', error);
      return {};
    }
  };

  /**
   * Guarda las categorías asignadas en localStorage
   * TODO PRODUCCIÓN: Reemplazar por POST /api/suppliers/:id/categories
   */
  const saveAssignedCategoriesToStorage = (categories: Record<string, string[]>) => {
    try {
      localStorage.setItem(STORAGE_KEYS.ASSIGNED_CATEGORIES, JSON.stringify(categories));
    } catch (error) {
      console.error('Error saving assigned categories to localStorage:', error);
    }
  };

  /**
   * Carga las notas desde localStorage
   * TODO PRODUCCIÓN: Reemplazar por GET /api/suppliers/:id/notes
   */
  const loadNotesFromStorage = (): Record<string, any[]> => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SUPPLIER_NOTES);
      if (!stored) return {};
      
      const parsed = JSON.parse(stored);
      // Convertir las fechas de string a Date objects
      Object.keys(parsed).forEach(supplierId => {
        parsed[supplierId] = parsed[supplierId].map((note: any) => ({
          ...note,
          createdAt: new Date(note.createdAt),
        }));
      });
      return parsed;
    } catch (error) {
      console.error('Error loading notes from localStorage:', error);
      return {};
    }
  };

  /**
   * Guarda las notas en localStorage
   * TODO PRODUCCIÓN: Reemplazar por POST /api/notes (crear) o DELETE /api/notes/:id (eliminar)
   */
  const saveNotesToStorage = (notes: Record<string, any[]>) => {
    try {
      localStorage.setItem(STORAGE_KEYS.SUPPLIER_NOTES, JSON.stringify(notes));
    } catch (error) {
      console.error('Error saving notes to localStorage:', error);
    }
  };

  // ============================================================================
  // FIN FUNCIONES DE PERSISTENCIA
  // ============================================================================

  // Cargar datos iniciales
  useEffect(() => {
    loadData();
    // Cargar notas desde localStorage al iniciar
    const storedNotes = loadNotesFromStorage();
    setNotes(storedNotes);
  }, []);

  useEffect(() => {
    filterSuppliers();
  }, [searchTerm, suppliers]);

  // Persistir notas cuando cambien
  // WARNING: En producción esto debería ser una llamada a API, no un efecto automático
  useEffect(() => {
    saveNotesToStorage(notes);
  }, [notes]);

  const loadData = async () => {
    try {
      setLoading(true);
      await dataService.loadPurchaseOrders('/PurchaseOrders.csv');
      
      // Obtener datos del Supply Master que tienen más info
      const supplierMasterData = dataService.getSupplierMasterData();
      
      // WARNING: Cargar categorías asignadas desde localStorage
      // TODO PRODUCCIÓN: Cargar desde API GET /api/suppliers/categories
      const storedCategories = loadAssignedCategoriesFromStorage();
      
      // Convertir a SupplierData con campos adicionales
      const supplierData: SupplierData[] = supplierMasterData.map(sm => {
        const assignedCats = storedCategories[sm.id] || [];
        return {
          ...sm,
          supplier: sm.razonSocial,
          orderCount: sm.ordenes,
          assignedCategories: assignedCats,
          categoriesCount: assignedCats.length > 0 ? assignedCats.length : 1,
          locationsCount: sm.ubicaciones.length,
          firstOrderDate: new Date(sm.primeraOrdenFecha),
          lastOrderDate: new Date(sm.ultimaOrdenFecha),
        };
      });
      
      // Cargar todas las categorías disponibles del Supply Master
      const categories = dataService.getCategories();
      setAvailableCategories(categories);
      
      setSuppliers(supplierData);
      setFilteredSuppliers(supplierData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar los datos. Verifica que el archivo CSV esté disponible.');
      setLoading(false);
    }
  };

  const filterSuppliers = () => {
    if (!searchTerm.trim()) {
      setFilteredSuppliers(suppliers);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = suppliers.filter(sup =>
      sup.razonSocial.toLowerCase().includes(term)
    );
    setFilteredSuppliers(filtered);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (supplier: string) => {
    setSelectedSupplier(supplier);
    setDetailDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDetailDialogOpen(false);
    setSelectedSupplier(null);
  };
  
  // Handlers para asignación de categorías
  const handleOpenCategoryDialog = () => {
    if (selected.size === 0) {
      return;
    }
    setCategoryDialogOpen(true);
    setSelectedCategories([]);
  };
  
  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setSelectedCategories([]);
  };
  
  const handleAssignCategories = () => {
    // WARNING: Actualizar categorías y persistir en localStorage
    // TODO PRODUCCIÓN: Hacer POST /api/suppliers/:id/categories para cada proveedor seleccionado
    
    // Actualizar las categorías asignadas a los proveedores seleccionados
    const updatedSuppliers = suppliers.map(sup => {
      if (selected.has(sup.id)) {
        return {
          ...sup,
          assignedCategories: selectedCategories,
          categoriesCount: selectedCategories.length,
        };
      }
      return sup;
    });
    
    // Construir objeto con categorías para persistir
    const categoriesToSave: Record<string, string[]> = {};
    updatedSuppliers.forEach(sup => {
      if (sup.assignedCategories && sup.assignedCategories.length > 0) {
        categoriesToSave[sup.id] = sup.assignedCategories;
      }
    });
    
    // Guardar en localStorage
    saveAssignedCategoriesToStorage(categoriesToSave);
    
    setSuppliers(updatedSuppliers);
    setFilteredSuppliers(updatedSuppliers.filter(sup => 
      !searchTerm || sup.razonSocial.toLowerCase().includes(searchTerm.toLowerCase())
    ));
    
    handleCloseCategoryDialog();
  };
  
  // Handlers del sistema de notas
  const handleAddNote = () => {
    if (!selectedSupplier || !newNoteContent.trim()) return;
    
    // WARNING: Usuario hardcoded - En producción obtener del sistema de autenticación
    // TODO PRODUCCIÓN: 
    // 1. Obtener usuario actual de useAuth() o contexto de autenticación
    // 2. Hacer POST /api/suppliers/:id/notes con { content, category, userId }
    // 3. El backend debe agregar timestamp del servidor (createdAt)
    const newNote = {
      id: `note-${Date.now()}`,
      supplierId: selectedSupplier,
      content: newNoteContent,
      createdBy: 'Usuario Actual', // TODO: Obtener de sistema de autenticación
      createdAt: new Date(),
      category: newNoteCategory,
    };
    
    setNotes(prev => ({
      ...prev,
      [selectedSupplier]: [...(prev[selectedSupplier] || []), newNote]
    }));
    // La persistencia se maneja automáticamente por el useEffect que observa 'notes'
    
    setNewNoteContent('');
    setNewNoteCategory('general');
  };
  
  const handleDeleteNote = (noteId: string) => {
    if (!selectedSupplier) return;
    
    // WARNING: Eliminar nota de localStorage
    // TODO PRODUCCIÓN: Hacer DELETE /api/notes/:noteId
    setNotes(prev => ({
      ...prev,
      [selectedSupplier]: (prev[selectedSupplier] || []).filter(note => note.id !== noteId)
    }));
    // La persistencia se maneja automáticamente por el useEffect que observa 'notes'
  };
  
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };
  
  const handleCloseDialogWithReset = () => {
    handleCloseDialog();
    setCurrentTab(0);
    setNewNoteContent('');
    setNewNoteCategory('general');
  };
  
  const getNoteCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      general: 'General',
      commercial: 'Comercial',
      quality: 'Calidad',
      logistics: 'Logística',
      financial: 'Financiero',
    };
    return labels[category] || category;
  };
  
  const getNoteCategoryColor = (category: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colorMap: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
      general: 'default',
      commercial: 'primary',
      quality: 'success',
      logistics: 'info',
      financial: 'warning',
    };
    return colorMap[category] || 'default';
  };
  
  // Exportación a Excel
  const handleExportToExcel = () => {
    // Preparar datos para exportación
    const dataToExport = (selected.size > 0 
      ? filteredSuppliers.filter(s => selected.has(s.id))
      : filteredSuppliers
    ).map(sup => ({
      'Razón Social': sup.razonSocial,
      'Nombre': sup.nombreRepresentante,
      'Apellido': sup.apellidoRepresentante,
      'Teléfono': sup.telefonoRepresentante,
      'Email': sup.emailRepresentante,
      'Órdenes de Compra': sup.orderCount,
      'Categorías Asignadas': sup.assignedCategories?.length || 0,
      'Ubicaciones': sup.locationsCount,
      'Primera Orden': formatDate(sup.firstOrderDate),
      'Última Orden': formatDate(sup.lastOrderDate),
      'Categorías del SM': sup.assignedCategories?.join(', ') || '',
    }));
    
    // Crear workbook y worksheet
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Proveedores');
    
    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 30 }, // Razón Social
      { wch: 15 }, // Nombre
      { wch: 15 }, // Apellido
      { wch: 15 }, // Teléfono
      { wch: 25 }, // Email
      { wch: 12 }, // Órdenes
      { wch: 15 }, // Categorías Asignadas
      { wch: 12 }, // Ubicaciones
      { wch: 15 }, // Primera Orden
      { wch: 15 }, // Última Orden
      { wch: 50 }, // Categorías del SM
    ];
    ws['!cols'] = colWidths;
    
    // Generar archivo
    const fileName = `proveedores_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };
  
  // Handlers de selección
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = new Set(filteredSuppliers.map(s => s.id));
      setSelected(newSelected);
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.has(id);
  
  // Handlers de ordenamiento
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  const sortedSuppliers = React.useMemo(() => {
    return [...filteredSuppliers].sort((a, b) => {
      let aValue: any = a[orderBy as keyof SupplierData];
      let bValue: any = b[orderBy as keyof SupplierData];
      
      // Manejar fechas
      if (orderBy === 'firstOrderDate' || orderBy === 'lastOrderDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [filteredSuppliers, order, orderBy]);

  const formatCurrency = (amount: number) => {
    // Formato argentino: punto para miles, sin decimales
    return '$' + Math.round(amount).toLocaleString('es-AR');
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getSupplierDetails = () => {
    if (!selectedSupplier) return null;

    const orders = dataService.getPurchaseOrdersBySupplier(selectedSupplier);
    const categories = Array.from(new Set(orders.map(po => po.item)));
    const locations = Array.from(new Set(orders.map(po => po.location)));
    const classes = Array.from(new Set(orders.map(po => po.class).filter(Boolean)));
    const trades = Array.from(new Set(orders.map(po => po.trade).filter(Boolean)));

    // Calcular gasto por categoría
    const spendByCategory = categories.map(cat => ({
      category: cat,
      spend: orders
        .filter(po => po.item === cat)
        .reduce((sum, po) => sum + po.amountUSD, 0),
      orderCount: orders.filter(po => po.item === cat).length,
    })).sort((a, b) => b.spend - a.spend);

    return {
      orders,
      categories,
      locations,
      classes,
      trades,
      spendByCategory,
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Proveedores actuales
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const details = selectedSupplier ? getSupplierDetails() : null;
  const supplierData = suppliers.find(s => s.supplier === selectedSupplier);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" gutterBottom>
            Proveedores actuales
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {suppliers.length} proveedores activos con historial de compras
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportToExcel}
        >
          Exportar {selected.size > 0 ? `(${selected.size})` : `(${filteredSuppliers.length})`}
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Box p={2}>
          <TextField
            fullWidth
            placeholder="Buscar proveedor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.size > 0 && selected.size < filteredSuppliers.length}
                  checked={filteredSuppliers.length > 0 && selected.size === filteredSuppliers.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'razonSocial'}
                  direction={orderBy === 'razonSocial' ? order : 'asc'}
                  onClick={() => handleRequestSort('razonSocial')}
                >
                  <strong>Proveedor</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell><strong>Nombre</strong></TableCell>
              <TableCell><strong>Apellido</strong></TableCell>
              <TableCell><strong>Teléfono</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'orderCount'}
                  direction={orderBy === 'orderCount' ? order : 'asc'}
                  onClick={() => handleRequestSort('orderCount')}
                >
                  <strong>Órdenes</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'categoriesCount'}
                  direction={orderBy === 'categoriesCount' ? order : 'asc'}
                  onClick={() => handleRequestSort('categoriesCount')}
                >
                  <strong>Categorías</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'locationsCount'}
                  direction={orderBy === 'locationsCount' ? order : 'asc'}
                  onClick={() => handleRequestSort('locationsCount')}
                >
                  <strong>Ubicaciones</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'firstOrderDate'}
                  direction={orderBy === 'firstOrderDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('firstOrderDate')}
                >
                  <strong>Primera Orden</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'lastOrderDate'}
                  direction={orderBy === 'lastOrderDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('lastOrderDate')}
                >
                  <strong>Última Orden</strong>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSuppliers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((sup) => {
                const isItemSelected = isSelected(sup.id);
                
                return (
                  <TableRow
                    key={sup.id}
                    hover
                    onClick={() => handleSelectOne(sup.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isItemSelected} />
                    </TableCell>
                    <TableCell 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(sup.supplier);
                      }}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {sup.supplier}
                    </TableCell>
                    <TableCell>{sup.nombreRepresentante}</TableCell>
                    <TableCell>{sup.apellidoRepresentante}</TableCell>
                    <TableCell>{sup.telefonoRepresentante}</TableCell>
                    <TableCell>{sup.emailRepresentante}</TableCell>
                    <TableCell align="right">{sup.orderCount.toLocaleString('es-AR')}</TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                        {sup.categoriesCount}
                        {sup.assignedCategories && sup.assignedCategories.length > 0 && (
                          <Tooltip 
                            title={
                              <Box>
                                <Typography variant="body2" fontWeight="bold" mb={0.5}>
                                  Categorías Asignadas:
                                </Typography>
                                {sup.assignedCategories.map((cat, idx) => (
                                  <Typography key={idx} variant="caption" display="block">
                                    • {cat}
                                  </Typography>
                                ))}
                              </Box>
                            }
                          >
                            <CheckCircleIcon 
                              sx={{ 
                                fontSize: 18, 
                                color: colors.success.main,
                                cursor: 'pointer'
                              }} 
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{sup.locationsCount}</TableCell>
                    <TableCell align="right">{formatDate(sup.firstOrderDate)}</TableCell>
                    <TableCell align="right">{formatDate(sup.lastOrderDate)}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredSuppliers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
        />
      </TableContainer>

      {/* Botón flotante para asignar categorías */}
      {selected.size > 0 && (
        <Fab
          color="primary"
          aria-label="asignar categorías"
          onClick={handleOpenCategoryDialog}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
        >
          <CategoryIcon />
        </Fab>
      )}

      {/* Dialog de Asignación de Categorías */}
      <Dialog
        open={categoryDialogOpen}
        onClose={handleCloseCategoryDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Asignar Categorías ({selected.size} {selected.size === 1 ? 'proveedor' : 'proveedores'})
            </Typography>
            <IconButton onClick={handleCloseCategoryDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={availableCategories}
              value={selectedCategories}
              onChange={(_, newValue) => setSelectedCategories(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categorías del Supply Master"
                  placeholder="Seleccionar categorías..."
                  helperText={`${availableCategories.length} categorías disponibles`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    color="primary"
                  />
                ))
              }
            />
          </FormControl>
          
          {selectedCategories.length > 0 && (
            <Box mt={3}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Se asignarán {selectedCategories.length} categorías a {selected.size} {selected.size === 1 ? 'proveedor' : 'proveedores'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCategoryDialog}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAssignCategories} 
            variant="contained"
            disabled={selectedCategories.length === 0}
          >
            Asignar Categorías
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Detalle del Proveedor */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDialogWithReset}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component="div">
              {selectedSupplier}
            </Typography>
            <IconButton onClick={handleCloseDialogWithReset} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {details && supplierData && (
            <Box>
              {/* Tabs */}
              <Tabs 
                value={currentTab} 
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
              >
                <Tab 
                  icon={<InfoIcon />} 
                  iconPosition="start" 
                  label="Información" 
                />
                <Tab 
                  icon={<NoteIcon />} 
                  iconPosition="start" 
                  label={`Notas (${(notes[selectedSupplier || ''] || []).length})`}
                />
              </Tabs>
              
              {/* Tab Panel: Información */}
              {currentTab === 0 && (
                <Box>
                  {/* KPIs principales */}
                  <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Órdenes de Compra
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {supplierData.orderCount.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <CategoryIcon sx={{ mr: 1, color: colors.brand.blue }} />
                        <Typography variant="body2" color="text.secondary">
                          Categorías
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {supplierData.categoriesCount}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <LocationOnIcon sx={{ mr: 1, color: colors.brand.blue }} />
                        <Typography variant="body2" color="text.secondary">
                          Ubicaciones
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {supplierData.locationsCount}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Categorías Asignadas del Supply Master */}
              {supplierData.assignedCategories && supplierData.assignedCategories.length > 0 && (
                <>
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                      Categorías Asignadas del Supply Master ({supplierData.assignedCategories.length})
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {supplierData.assignedCategories.map((category, idx) => (
                        <Chip
                          key={idx}
                          label={category}
                          color="success"
                          icon={<CategoryIcon />}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />
                </>
              )}

              {/* Gasto por Categoría */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Gasto por Categoría (Top 10)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Categoría</strong></TableCell>
                        <TableCell align="right"><strong>Órdenes</strong></TableCell>
                        <TableCell align="right"><strong>Gasto</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details.spendByCategory.slice(0, 10).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.category}</TableCell>
                          <TableCell align="right">{item.orderCount}</TableCell>
                          <TableCell align="right">{formatCurrency(item.spend)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Clases y Trades */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Clases
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {details.classes.map((cls, idx) => (
                      <Chip
                        key={idx}
                        label={cls}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Trades
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {details.trades.map((trade, idx) => (
                      <Chip
                        key={idx}
                        label={trade}
                        color="secondary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Ubicaciones */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Ubicaciones ({details.locations.length})
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {details.locations.map((loc, idx) => (
                    <Chip
                      key={idx}
                      label={loc}
                      variant="outlined"
                      icon={<LocationOnIcon />}
                    />
                  ))}
                </Box>
              </Box>
                </Box>
              )}
              
              {/* Tab Panel: Notas */}
              {currentTab === 1 && (
                <Box>
                  {/* Formulario para agregar nueva nota */}
                  <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Agregar Nueva Nota
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={8}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Contenido de la nota"
                          placeholder="Escribe tu nota aquí..."
                          value={newNoteContent}
                          onChange={(e) => setNewNoteContent(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Categoría</InputLabel>
                          <Select
                            value={newNoteCategory}
                            label="Categoría"
                            onChange={(e) => setNewNoteCategory(e.target.value)}
                          >
                            <MenuItem value="general">General</MenuItem>
                            <MenuItem value="commercial">Comercial</MenuItem>
                            <MenuItem value="quality">Calidad</MenuItem>
                            <MenuItem value="logistics">Logística</MenuItem>
                            <MenuItem value="financial">Financiero</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAddNote}
                          disabled={!newNoteContent.trim()}
                        >
                          Agregar Nota
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                  
                  {/* Lista de notas */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Historial de Notas ({(notes[selectedSupplier || ''] || []).length})
                    </Typography>
                    
                    {(notes[selectedSupplier || ''] || []).length === 0 ? (
                      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                        <NoteIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          No hay notas para este proveedor
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Agrega la primera nota usando el formulario de arriba
                        </Typography>
                      </Paper>
                    ) : (
                      <List>
                        {[...(notes[selectedSupplier || ''] || [])].reverse().map((note) => (
                          <Paper 
                            key={note.id} 
                            variant="outlined" 
                            sx={{ mb: 2, p: 2 }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Chip 
                                  label={getNoteCategoryLabel(note.category)}
                                  color={getNoteCategoryColor(note.category)}
                                  size="small"
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {new Intl.DateTimeFormat('es-ES', {
                                    dateStyle: 'medium',
                                    timeStyle: 'short'
                                  }).format(new Date(note.createdAt))}
                                </Typography>
                              </Box>
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeleteNote(note.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              {note.content}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Por: {note.createdBy}
                            </Typography>
                          </Paper>
                        ))}
                      </List>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogWithReset} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActiveSuppliers;
